import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Phone, CalendarCheck, BadgeCheck, Clock } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { DOCTOR, whatsappLink } from "@/config/doctorConfig";
import { MaskedLine } from "@/components/motion";
import { scrollToSection } from "@/lib/scroll";

export const Hero = () => {
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [28, -28]);

  return (
    <section id="home" className="relative overflow-hidden bg-navy-900 grain" data-testid="hero-section">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.35]"
        style={{ background: "radial-gradient(60% 50% at 75% 30%, rgba(13,148,136,0.25) 0%, rgba(10,37,64,0) 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-36 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-400/10 px-4 py-1.5 mb-7"
            >
              <BadgeCheck size={14} className="text-teal-300" />
              <span className="text-xs font-mono tracking-wider text-teal-200 uppercase">{DOCTOR.qualifications}</span>
            </motion.div>

            <h1 className="font-serif font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight" data-testid="hero-headline">
              <MaskedLine delay={0.15}>Comprehensive Medical Care</MaskedLine>
              <MaskedLine delay={0.28}>with Expertise in</MaskedLine>
              <MaskedLine delay={0.41} className="text-teal-300">Internal Medicine</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
            >
              Providing thoughtful evaluation, evidence-based treatment and comprehensive care
              for adults with medical and lifestyle-related conditions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.6 }}
              className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400"
            >
              <span className="flex items-center gap-2">
                <MapPin size={15} className="text-teal-400" />
                {DOCTOR.hospitalName} · {DOCTOR.city}
              </span>
              <span className="flex items-center gap-2" data-testid="hero-opd-timings">
                <Clock size={15} className="text-teal-400" />
                OPD {DOCTOR.opdTime} · Mon–Fri · {DOCTOR.opdOff}
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <button
                onClick={() => scrollToSection("appointment")}
                data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-navy-900 bg-white hover:bg-teal-50 shadow-lift transition-colors duration-200"
              >
                <CalendarCheck size={16} /> Book an Appointment
              </button>
              <a
                href={DOCTOR.clinicPhoneHref}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white border border-white/25 hover:border-teal-300/60 hover:text-teal-200 transition-colors duration-200"
              >
                <Phone size={16} /> Call Now
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white bg-[#128C7E] hover:bg-[#0d6e63] transition-colors duration-200"
              >
                <WhatsAppIcon size={17} /> WhatsApp
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5" ref={frameRef}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-sm mx-auto lg:ml-auto"
            >
              <div aria-hidden="true" className="absolute -inset-3 rounded-[2rem] border border-teal-400/25" />
              <div aria-hidden="true" className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-teal-500/15 blur-2xl" />
              <motion.figure style={{ y }} className="relative rounded-[1.75rem] overflow-hidden shadow-frame bg-navy-800">
                <img
                  src={DOCTOR.photoHero}
                  alt="Dr. Suyash Bhadoriya — MBBS, MD (Internal Medicine), General Physician & Internal Medicine Specialist in Bhopal"
                  className="w-full h-auto object-cover"
                  width="900"
                  height="958"
                  fetchPriority="high"
                  data-testid="hero-doctor-photo"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-950/95 via-navy-950/60 to-transparent px-6 pt-14 pb-5">
                  <p className="font-serif font-bold text-white text-lg leading-tight">{DOCTOR.name}</p>
                  <p className="text-xs text-teal-300 font-mono tracking-wide mt-1">{DOCTOR.role} · {DOCTOR.designation}</p>
                </figcaption>
              </motion.figure>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
